import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack
} from "@mui/material";
import {
  AppstoreFilled,
  MenuOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { useTranslation } from "../../../contexts/TranslationContext";
import { FileMetadata } from "../../../api/types/cms";
import CmsFileList, { CmsFileListRef } from "../../cms/CmsFileList";

type CmsFileSelectionProps = {
  listRef?: React.MutableRefObject<CmsFileListRef | undefined>;
  hideSearch?: boolean;
  showModeSelector?: boolean;
  poolId?: string;
  onFileSelectionUpdate?: (selected: Array<FileMetadata>) => void;
  multiple?: boolean;
  preSelected?: Array<FileMetadata>;
};

export default function CmsFileSelection({
                                           listRef,
                                           poolId,
                                           hideSearch = false,
                                           showModeSelector = true,
                                           onFileSelectionUpdate = () => {
                                           },
                                           preSelected = [],
                                           multiple = false
                                         }: CmsFileSelectionProps) {
  const { translate } = useTranslation();

  const [mode, changeMode] = useState<"list" | "masonry">("masonry");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedFiles, setSelectedFiles] =
    useState<Array<FileMetadata>>(preSelected);

  useEffect(
    () => onFileSelectionUpdate(selectedFiles),
    [onFileSelectionUpdate, selectedFiles]
  );

  const onFileSelection = (item: FileMetadata) => {
    if (multiple) {
      setSelectedFiles(draft => {
        const found = Boolean(draft.find(i => i.id === item.id));
        if (found) {
          return draft.filter(i => i.id !== item.id);
        } else {
          return [...draft, item];
        }
      });
    } else {
      setSelectedFiles(draft => {
        const found = Boolean(draft.find(i => i.id === item.id));
        if (found) {
          return [];
        } else {
          return [item];
        }
      });
    }
  };

  return (
    <>
      <Paper
        variant="elevation"
        elevation={4}
        component="div"
        sx={{ position: "sticky", top: -8, zIndex: 100, p: 2 }}>
        {hideSearch && showModeSelector && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end">
            <ButtonGroup size="small" color="primary">
              <Button
                variant={
                  mode === "list" ? "contained" : "outlined"
                }
                startIcon={<MenuOutlined />}
                onClick={() => changeMode("list")}>
                {translate("pages.cms.files.view_mode.list")}
              </Button>
              <Button
                variant={
                  mode === "masonry"
                    ? "contained"
                    : "outlined"
                }
                startIcon={<AppstoreFilled />}
                onClick={() => changeMode("masonry")}>
                {translate("pages.cms.files.view_mode.card")}
              </Button>
            </ButtonGroup>
          </Stack>
        )}
        {!hideSearch && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <OutlinedInput
                size="medium"
                id="file-search"
                value={searchQuery}
                autoComplete="off"
                aria-autocomplete="none"
                autoCapitalize="off"
                autoCorrect="off"
                startAdornment={
                  <InputAdornment
                    position="start"
                    sx={{ mr: -0.5 }}>
                    <SearchOutlined />
                  </InputAdornment>
                }
                fullWidth
                onChange={event =>
                  setSearchQuery(event.target.value)
                }
                aria-describedby="header-search-text"
                inputProps={{
                  "aria-label": "weight",
                  style: {
                    height: "100%"
                  }
                }}
                sx={{ height: "100%" }}
                placeholder={translate(
                  "static.search.placeholder"
                )}
              />
            </FormControl>
            {showModeSelector && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end">
                <ButtonGroup size="medium" color="primary">
                  <Button
                    variant={
                      mode === "list"
                        ? "contained"
                        : "outlined"
                    }
                    startIcon={<MenuOutlined />}
                    onClick={() => changeMode("list")}>
                    {translate(
                      "pages.cms.files.view_mode.list"
                    )}
                  </Button>
                  <Button
                    variant={
                      mode === "masonry"
                        ? "contained"
                        : "outlined"
                    }
                    startIcon={<AppstoreFilled />}
                    onClick={() => changeMode("masonry")}>
                    {translate(
                      "pages.cms.files.view_mode.card"
                    )}
                  </Button>
                </ButtonGroup>
              </Stack>
            )}
          </Stack>
        )}
      </Paper>
      <Stack px={2}>
        <CmsFileList
          listRef={listRef}
          mode={mode}
          searchQuery={searchQuery}
          poolId={poolId}
          mediaQueries={[
            "(min-width:1000px)",
            "(min-width:600px)",
            "(min-width:400px)"
          ]}
          onClick={onFileSelection}
          selectedFiles={selectedFiles}
        />
      </Stack>
    </>
  );
}
